import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useLeggTilLenke, type ExternalLink } from '../api/useHypoteseLenker.ts';
import type { Hypothesis } from '../api/useHypotheses.ts';
import { Dialog } from '../skall/Dialog.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Tekstknapp } from './deler.tsx';

/**
 * H3.5 del A — lenker på hypotesen (konseptsiden skjerm 1–7; P10, P11).
 *
 *   Lenker  2 av 3                                       Legg til lenke
 *   ⛓ Jira · NORD-412 ↗
 *     nordlys.atlassian.net                                        Fjern
 *
 * Der arbeidet med hypotesen faktisk skjer, i Jira eller Linear. Navnet er lenken, vertsnavnet står
 * under så leseren ser hvor den går før hen trykker — ingen favikon, panelet henter aldri noe fra
 * adressen (K8). Lenken åpnes i ny fane uten referrer: panelets adresse med hypotesenummeret skal
 * ikke lekke til Jira (P11). Teksten rendres som tekst.
 *
 * Høyst tre. På den tredje er «Legg til lenke» byttet med låsen og grunnen i overskriftens kant —
 * aldri en stum, deaktivert knapp. «Fjern» holdes tilbake i fem sekunder (DD-70), i tekstfarge: den
 * kan angres, og den rammer ingen andre (DD-53). Antallet vises bare til den som kan legge til; for
 * den som bare leser, finnes seksjonen ikke før det er en lenke å vise.
 *
 * Ingen tast: L er «Legg til saker …» i Grunnlag (H3.1), og to handlinger på samme tast går ikke.
 */

export const LENKE_TAK = 3;
const NAVN_MAKS = 40;
const ADRESSE_MAKS = 500;

/** Vertsnavnet uten «www.», eller adressen selv om den ikke kan leses. */
export function vertAv(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Adressen som den skal stå på papir: uten «https://» foran (P9). */
export const adresseUtenSkjema = (url: string) => url.replace(/^https:\/\//i, '');

/** Klientens speil av kontraktens regel (P11). Tjeneren er autoriteten; dette er låsen FØR kallet. */
function adresseFeil(adresse: string): string | null {
  const a = adresse.trim();
  if (!a) return 'Skriv adressen til lenken.';
  if (!/^https:\/\//i.test(a)) return 'Adressen må begynne med https://.';
  let u: URL;
  try {
    u = new URL(a);
  } catch {
    return 'Adressen må begynne med https://.';
  }
  if (u.username || u.password) return 'Adressen kan ikke ha brukernavn eller passord i seg.';
  if (a.length > ADRESSE_MAKS) return `Adressen kan ha høyst ${ADRESSE_MAKS} tegn.`;
  return null;
}

function navnFeil(navn: string): string | null {
  const n = navn.trim();
  if (!n) return 'Skriv et navn på lenken.';
  if (n.length > NAVN_MAKS) return 'Navnet kan ha høyst 40 tegn.';
  return null;
}

/**
 * P10: hypotesens eier, vurderer og beslutningstaker, og behandler og eier i rommet — i alle
 * statuser unntatt utkast og trukket, også etter konklusjonen.
 */
export const kanEndreLenker = (h: Hypothesis, megId: string | undefined, behandler: boolean): boolean =>
  h.status !== 'utkast' && h.status !== 'trukket' && (behandler || (megId !== undefined && [h.owner?.id, h.reviewer?.id, h.decider?.id].includes(megId)));

export interface LenkerProps {
  h: Hypothesis;
  kanEndre: boolean;
  tittelTag: 'h2' | 'h3';
  /** Lenken som venter i angre-vinduet — raden står ikke så lenge. */
  fjernes: string | null;
  onFjern: (l: ExternalLink) => void;
  onKvittering: (tekst: string) => void;
}

type Serverfeil = { slag: 'kom-foerst' } | { slag: 'nett' } | { slag: 'annet'; tekst: string } | null;

export function LenkerSeksjon({ h, kanEndre, tittelTag, fjernes, onFjern, onKvittering }: LenkerProps) {
  const smal = useSmal();
  const alle = h.externalLinks ?? [];
  const synlige = alle.filter((l) => l.id !== fjernes);
  const n = synlige.length;
  const [aapen, setAapen] = useState(false);
  const knapp = useRef<HTMLButtonElement>(null);
  const Tittel = tittelTag;
  const tittelId = `hl-tittel-${h.id}`;

  if (!kanEndre && n === 0) return null;

  const fullt = n >= LENKE_TAK;
  const lukk = () => {
    setAapen(false);
    setTimeout(() => knapp.current?.focus(), 0);
  };
  const leggTilKnapp = (
    <button ref={knapp} type="button" className="panel-knapp-lenke hb-sek-lenke" aria-haspopup={smal ? 'dialog' : undefined} aria-expanded={aapen} onClick={() => setAapen(true)}>
      Legg til lenke
    </button>
  );

  let kant: ReactNode = null;
  if (kanEndre && fullt) {
    kant = (
      <span className="hl-laas" data-testid="hl-laas">
        <Ikon navn="lock-closed-outline" />
        Tre er taket. Fjern én for å legge til en ny.
      </span>
    );
  } else if (kanEndre && !aapen && n > 0) {
    kant = leggTilKnapp;
  }

  const skjema = aapen ? <LenkeSkjema h={h} n={n} smal={smal} onLukk={lukk} onLagt={(navn) => { lukk(); onKvittering(`Lenken «${navn}» er lagt til.`); }} /> : null;

  return (
    <section className="hb-sek hl" aria-labelledby={tittelId} data-testid="hl-lenker">
      <div className="hb-sek-hode">
        <span className="hl-hode-v">
          <Tittel id={tittelId} className="hb-sek-tittel">
            Lenker
          </Tittel>
          {kanEndre && (n > 0 || aapen) ? <span className="hb-dempet hb-liten">{`${n} av ${LENKE_TAK}`}</span> : null}
        </span>
        {kant}
      </div>
      {n === 0 && !aapen ? (
        <div className="hb-notis-tom" data-testid="hl-tom">
          <p>Ingen lenker ennå. Legg til saken i Jira eller Linear, så finner de andre arbeidet herfra.</p>
          <div>{leggTilKnapp}</div>
        </div>
      ) : null}
      {n > 0 ? (
        <ul className="hl-liste" aria-label="Lenkene">
          {synlige.map((l) => (
            <LenkeRad key={l.id} l={l} smal={smal} kanFjerne={kanEndre && !smal} onFjern={() => onFjern(l)} />
          ))}
        </ul>
      ) : null}
      {skjema}
    </section>
  );
}

function LenkeRad({ l, smal, kanFjerne, onFjern }: { l: ExternalLink; smal: boolean; kanFjerne: boolean; onFjern: () => void }) {
  const innhold = (
    <>
      <Ikon navn="link-outline" className="hl-ikon" />
      <span className="hl-tekst">
        <span className="hl-navn">
          {l.label}
          <Ikon navn="open-outline" className="hl-ut" />
          <span className="panel-skjult"> (åpnes i ny fane)</span>
        </span>
        <span className="hl-vert">{vertAv(l.url)}</span>
      </span>
    </>
  );
  if (smal) {
    // Under 900 px er HELE raden lenken, 48 høy (skjerm 6).
    return (
      <li data-testid={`lenke-rad-${l.id}`}>
        <a href={l.url} target="_blank" rel="noopener noreferrer" className="hl-rad hl-rad-lenke">
          {innhold}
        </a>
      </li>
    );
  }
  return (
    <li className="hl-rad" data-testid={`lenke-rad-${l.id}`}>
      <Ikon navn="link-outline" className="hl-ikon" />
      <span className="hl-tekst">
        <a href={l.url} target="_blank" rel="noopener noreferrer" className="hl-navn hl-navn-lenke">
          {l.label}
          <Ikon navn="open-outline" className="hl-ut" />
          <span className="panel-skjult"> (åpnes i ny fane)</span>
        </a>
        <span className="hl-vert">{vertAv(l.url)}</span>
      </span>
      {kanFjerne ? (
        <span className="hl-k">
          <button type="button" className="panel-knapp-lenke hb-noeytral" aria-label={`Fjern ${l.label}`} onClick={onFjern}>
            Fjern
          </button>
        </span>
      ) : null}
    </li>
  );
}

/**
 * Skjemaet (skjerm 1, 5, 7): på stedet over 900 px, et ark fra bunnen under. To felt, «Legg til» som
 * eneste fylte knapp. Feltene merkes først etter første forsøk (validering etter interaksjon), med én
 * setning per felt. Det som er skrevet, står alltid — også når noen kom først, og uten nett.
 */
function LenkeSkjema({ h, n, smal, onLukk, onLagt }: { h: Hypothesis; n: number; smal: boolean; onLukk: () => void; onLagt: (navn: string) => void }) {
  const leggTil = useLeggTilLenke();
  const [adresse, setAdresse] = useState('');
  const [navn, setNavn] = useState('');
  const [forsokt, setForsokt] = useState(false);
  const [feil, setFeil] = useState<Serverfeil>(null);
  const forste = useRef<HTMLInputElement>(null);
  const id = `hl-skjema-${h.id}`;

  useEffect(() => {
    if (!smal) forste.current?.focus();
  }, [smal]);

  const aFeil = forsokt ? adresseFeil(adresse) : null;
  const nFeil = forsokt ? navnFeil(navn) : null;
  const lengde = navn.trim().length;
  const teller = lengde >= NAVN_MAKS * 0.8 ? `${lengde} av ${NAVN_MAKS} tegn` : null;

  const send = () => {
    setForsokt(true);
    if (leggTil.isPending || adresseFeil(adresse) || navnFeil(navn)) return;
    setFeil(null);
    const ord = navn.trim();
    leggTil
      .mutateAsync({ hypoteseId: h.id, nummer: h.number, body: { url: adresse.trim(), label: ord } })
      .then(() => onLagt(ord))
      .catch((e: unknown) => {
        if (erApiFeil(e) && e.code === 'external_link_limit_reached') setFeil({ slag: 'kom-foerst' });
        else if (erNettfeil(e)) setFeil({ slag: 'nett' });
        else setFeil({ slag: 'annet', tekst: erApiFeil(e) ? e.brukertekst : 'Lenken ble ikke lagret. Prøv igjen.' });
      });
  };

  const paaTast = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      send();
    } else if (e.key === 'Escape' && !smal) {
      e.preventDefault();
      e.stopPropagation();
      onLukk();
    }
  };

  const felt = (
    <>
      <div className="h-felt">
        <label htmlFor={`${id}-adresse`} className="h-etikett">
          Adresse
        </label>
        <input
          ref={forste}
          id={`${id}-adresse`}
          className="h-input"
          type="url"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="https://"
          value={adresse}
          aria-invalid={aFeil ? true : undefined}
          aria-describedby={aFeil ? `${id}-adresse-feil` : undefined}
          onChange={(e) => setAdresse(e.target.value)}
        />
        {aFeil ? (
          <span id={`${id}-adresse-feil`} className="h-felt-grunn">
            <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
            {aFeil}
          </span>
        ) : null}
      </div>
      <div className="h-felt hl-navnfelt">
        <span className="h-felt-hode">
          <label htmlFor={`${id}-navn`} className="h-etikett">
            Navn
          </label>
          {teller ? (
            <span className={lengde > NAVN_MAKS ? 'h-teller h-teller-over' : 'h-teller'} role="status">
              {teller}
            </span>
          ) : null}
        </span>
        <input
          id={`${id}-navn`}
          className="h-input"
          type="text"
          autoComplete="off"
          placeholder="For eksempel Jira · NORD-87"
          value={navn}
          aria-invalid={nFeil ? true : undefined}
          aria-describedby={nFeil ? `${id}-navn-feil` : undefined}
          onChange={(e) => setNavn(e.target.value)}
        />
        {nFeil ? (
          <span id={`${id}-navn-feil`} className="h-felt-grunn">
            <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
            {nFeil}
          </span>
        ) : null}
      </div>
    </>
  );

  const melding =
    feil?.slag === 'kom-foerst' ? (
      <p role="alert" className="hl-melding">
        <Ikon navn="information-circle-outline" />
        <span>Noen la til en lenke mens du skrev, og nå er det tre. Det du skrev, står.</span>
      </p>
    ) : feil?.slag === 'nett' ? (
      <p role="alert" className="hl-melding">
        <Ikon navn="cloud-offline-outline" />
        <span>Lenken ble ikke lagret. Det du skrev, står.</span>
        <Tekstknapp onClick={send}>Prøv igjen</Tekstknapp>
      </p>
    ) : feil?.slag === 'annet' ? (
      <p role="alert" className="hl-melding">
        <Ikon navn="warning-outline" />
        <span>{feil.tekst}</span>
      </p>
    ) : null;

  const primar = (
    <button type="button" className="panel-knapp hl-legg-til" aria-busy={leggTil.isPending || undefined} aria-keyshortcuts="Control+Enter" onClick={send}>
      Legg til
      {smal ? null : <kbd>Ctrl ↵</kbd>}
    </button>
  );

  if (smal) {
    return (
      createPortal(
        <Dialog tittelId={`${id}-tittel`} onLukk={onLukk} className="h-ark" fokusFørst={forste} testid="hl-ark">
          <div className="hl-ark" onKeyDown={paaTast}>
            <div className="hl-ark-hode">
              <h2 id={`${id}-tittel`} className="panel-h2">
                Legg til lenke
              </h2>
              <button type="button" className="hb-stripe-knapp" aria-label="Lukk" onClick={onLukk}>
                <Ikon navn="close-outline" />
              </button>
            </div>
            {felt}
            {melding}
            <p className="hb-dempet hb-liten">{`${Math.min(n + 1, LENKE_TAK)} av ${LENKE_TAK} etter denne.`}</p>
            <div className="hl-ark-fot">
              {primar}
              <button type="button" className="panel-knapp-lenke" onClick={onLukk}>
                Lukk
              </button>
            </div>
          </div>
        </Dialog>,
        document.body,
      )
    );
  }

  return (
    <div className="hl-skjema" role="group" aria-label="Legg til lenke" onKeyDown={paaTast} data-testid="hl-skjema">
      {felt}
      {melding}
      <div className="hl-skjema-fot">
        {primar}
        <button type="button" className="panel-knapp-lenke" onClick={onLukk}>
          Lukk <kbd>Esc</kbd>
        </button>
      </div>
    </div>
  );
}
